import { useToast } from '@chakra-ui/react';
import { FormikErrors, FormikHelpers } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { isFetchBaseQueryError } from 'rtkQuery/isFetchBaseQueryError';
import { isSharesproFetchQueryError } from 'rtkQuery/sharespro/isSharesproFetchQueryError';
import { SharesproResponseFieldError } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import { isErrorWithMessage } from 'utils/isErrorWithMessage';

const cleanErrorMessage = (message: string | null): string | null => {
  if (!message) return message;

  const arrayCount = (message.match(/Array\s*\(/g) ?? []).length;

  if (arrayCount > 0 && message.includes('=>')) {
    const parts = message.split('=>');

    let lastArrayPartIndex = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (/Array\s*\(/.test(parts[i])) {
        lastArrayPartIndex = i;
        break;
      }
    }

    if (lastArrayPartIndex !== -1 && lastArrayPartIndex < parts.length - 1) {
      let value = parts.slice(lastArrayPartIndex + 1).join('=>').trim();

      let parensToRemove = arrayCount;

      while (parensToRemove > 0 && value.length > 0) {
        if (value.endsWith(')')) {
          value = value.slice(0, -1).trim();
          parensToRemove--;
        } else {
          break;
        }
      }

      if (value) return value;
    }
  }

  const match = message.match(/\[[^\]]+\] => (.*?)(?=\s\))/);
  if (match?.[1]) {
    return match[1];
  }

  return message;
};

const useFormError = <Values>(): ((
  error: any,
  formik: FormikHelpers<Values>
) => void) => {
  const toast = useToast();
  const { t } = useTranslation();

  return (error: any, formik: FormikHelpers<Values>) => {
    if (isSharesproFetchQueryError(error)) {
      const errorData = error.data as any;
      const fieldErrors = errorData.field_errors;
      if (Array.isArray(fieldErrors) && !isEmpty(fieldErrors)) {
        const fieldErrorsObj: Record<string, string> = {};
        fieldErrors.forEach((field: SharesproResponseFieldError) => {
          fieldErrorsObj[field.field] = field.error;
        });

        formik.setErrors(fieldErrorsObj as FormikErrors<Values>);
      } else {
        toast({
          title: t('error'),
          description: cleanErrorMessage(errorData.error_message),
          status: 'error',
          position: 'top-right',
          isClosable: true,
        });
      }
    } else {
      let errorText;
      if (isErrorWithMessage(error)) {
        errorText = error.message;
      } else if (
        isFetchBaseQueryError(error) &&
        error.status === 'FETCH_ERROR'
      ) {
        errorText = t('checkYourConnectionError');
      }

      toast({
        title: t('error'),
        description: errorText,
        status: 'error',
        position: 'top-right',
        isClosable: true,
      });
    }
  };
};

export default useFormError;
